import { Observable } from 'rxjs';
import type { Auth, User } from 'firebase/auth';
import { onAuthStateChanged, onIdTokenChanged } from 'firebase/auth';

/**
 * rxjs adapters replacing `@angular/fire/auth`'s `authState`/`user`/`idToken`.
 *
 * Each wraps the SDK's callback subscription in an `Observable`; the SDK returns
 * an unsubscribe function which the `Observable` uses as teardown. Apps are
 * zoneless and consume these via `toSignal`, so the only requirement is that the
 * stream emits — signal writes drive change detection without zones, so we must
 * NOT wrap callbacks in `NgZone`/`runInInjectionContext`.
 */

/** Mirrors `@angular/fire`'s `authState(auth)` → `onAuthStateChanged` (sign-in/out only). */
export function authState$(auth: Auth): Observable<User | null> {
  return new Observable<User | null>((subscriber) =>
    onAuthStateChanged(
      auth,
      (user) => subscriber.next(user),
      (error) => subscriber.error(error),
    ),
  );
}

/**
 * Mirrors `@angular/fire`'s `user$(auth)` → `onIdTokenChanged` (also re-emits on
 * token refresh). `user.service.ts#invalidateToken` relies on this re-emit, so
 * keep it mapped to `onIdTokenChanged`, NOT `onAuthStateChanged`.
 */
export function user$(auth: Auth): Observable<User | null> {
  return new Observable<User | null>((subscriber) =>
    onIdTokenChanged(
      auth,
      (user) => subscriber.next(user),
      (error) => subscriber.error(error),
    ),
  );
}

/** Mirrors `@angular/fire`'s `idToken(auth)` — the current ID token (or null). */
export function idToken$(auth: Auth): Observable<string | null> {
  return new Observable<string | null>((subscriber) =>
    onIdTokenChanged(
      auth,
      (user) => {
        void (async () => {
          try {
            subscriber.next(user ? await user.getIdToken() : null);
          } catch (error) {
            subscriber.error(error);
          }
        })();
      },
      (error) => subscriber.error(error),
    ),
  );
}
